import type {
  AttributeOptions,
  ClaimAttributeForm,
} from '@dispatch/shared/types';
import { v4 as uuidv4 } from 'uuid';

type ClaimAttributeRow = { attributeDefinitionIntegrationGUID: string };

export type IndexedField<TField> = { field: TField; index: number };

const normalizeName = (name: string): string => name.trim().toLowerCase();

/**
 * Matches configured display names to company attributes (case-insensitive),
 * in configuration order. Names with no matching attribute are dropped.
 */
export const resolvePinnedAttributes = (
  attributeNames: readonly string[],
  companyAttributes: readonly AttributeOptions[]
): AttributeOptions[] =>
  attributeNames.reduce<AttributeOptions[]>((resolved, name) => {
    const match = companyAttributes.find(
      (attribute) =>
        typeof attribute.title === 'string' &&
        normalizeName(attribute.title) === normalizeName(name)
    );

    return match && !resolved.includes(match) ? [...resolved, match] : resolved;
  }, []);

export const buildClaimAttributeRow = (
  attribute: AttributeOptions | undefined
): ClaimAttributeForm => ({
  integrationGUID: uuidv4(),
  attributeDefinitionIntegrationGUID: attribute?.value || '',
  displayName: attribute?.title || '',
  selectedAttributeValuesVarchars: [],
  selectedAttributeValuesBools: [],
  selectedAttributeValuesInts: [],
  selectedAttributeValuesFloats: [],
});

const hasRowFor = (
  rows: readonly ClaimAttributeRow[] | undefined,
  guid: string
): boolean =>
  (rows ?? []).some((row) => row.attributeDefinitionIntegrationGUID === guid);

export const buildMissingPinnedRows = (
  rows: readonly ClaimAttributeRow[] | undefined,
  pinnedAttributes: readonly AttributeOptions[]
): ClaimAttributeForm[] =>
  pinnedAttributes
    .filter((attribute) => !hasRowFor(rows, attribute.value))
    .map(buildClaimAttributeRow);

export const hasAllPinnedRows = (
  rows: readonly ClaimAttributeRow[] | undefined,
  pinnedGuids: readonly string[]
): boolean => pinnedGuids.every((guid) => hasRowFor(rows, guid));

/**
 * Splits field-array fields into pinned and other rows. Each entry keeps the
 * row's original index, because field names and errors are index-keyed.
 */
export const partitionPinnedFields = <TField>(
  fields: readonly TField[],
  isPinnedAt: (index: number) => boolean
): { pinned: IndexedField<TField>[]; others: IndexedField<TField>[] } => {
  const indexed = fields.map((field, index) => ({ field, index }));

  return {
    pinned: indexed.filter(({ index }) => isPinnedAt(index)),
    others: indexed.filter(({ index }) => !isPinnedAt(index)),
  };
};

/**
 * Add-attribute options: every company attribute that is neither pinned nor
 * already on the form.
 */
export const getAddableAttributeOptions = (
  companyAttributes: readonly AttributeOptions[],
  rows: readonly ClaimAttributeRow[] | undefined,
  pinnedGuids: readonly string[]
): { title: string; value: string }[] =>
  companyAttributes
    .filter(
      (attribute) =>
        !pinnedGuids.includes(attribute.value) &&
        !hasRowFor(rows, attribute.value)
    )
    .map(({ title, value }) => ({ title, value }));
